/**
 * DevOverlay Component
 * 
 * Mobile-friendly diagnostic overlay that appears when ?dev=1 is in URL.
 * Shows real-time mapping state, event trace, and system diagnostics.
 * 
 * Features:
 * - Lightweight floating button (top-right)
 * - Expandable overlay with diagnostic sections
 * - Event trace (last 20 events)
 * - Mapping object summary
 * - Copy-to-clipboard JSON export
 * - Safe for production (read-only, no PII)
 * - Auto-collapses when modals open
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getDiagnosticsState,
  subscribe,
  questionMappings,
  type MappingDiagnosticsState,
  type MappingLifecycleEvent,
  type QuestionMapping,
} from '../mapping';
import { ATLAS_DATA_CONTRACT_VERSION, ENGINE_RULESET_VERSION, UI_MAPPING_VERSION } from '../contracts/dataContract';
import { engineCapabilities, type EngineCapabilities } from './engineCapabilities';
import { fields as contractFields } from '../contract/atlasDataContract';
import {
  subscribeToOverlayState,
  notifyOverlayStateChange,
  buildDevUrl,
  computeMismatchReasons,
  formatVersionsSummary,
  SOURCE_FILE_PATHS,
} from './DevSidebar';

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get current docId from URL or other sources
 */
function getCurrentDocId(): string | null {
  if (typeof window === 'undefined') return null;
  
  // Check URL params
  const urlParams = new URLSearchParams(window.location.search);
  const docIdParam = urlParams.get('docId');
  if (docIdParam) return docIdParam;
  
  // Check URL path (e.g., /story/ui/doc_abc123)
  const pathMatch = window.location.pathname.match(/\/(doc_[a-zA-Z0-9]+)/);
  if (pathMatch) return pathMatch[1];
  
  return null;
}

/**
 * Check dev mode and get reasoning
 */
function getDevModeInfo(): { active: boolean; reason: string } {
  if (typeof window === 'undefined') {
    return { active: false, reason: 'Server-side render' };
  }
  
  const urlParams = new URLSearchParams(window.location.search);
  const devParam = urlParams.get('dev');
  
  if (devParam === '0') {
    return { active: false, reason: 'Explicitly disabled via ?dev=0' };
  }
  
  if (devParam === '1') {
    return { active: true, reason: '?dev=1 found in URL' };
  }
  
  const localFlag = localStorage.getItem('devtools') === '1';
  if (localFlag) {
    return { active: true, reason: 'localStorage.devtools=1 persisted' };
  }
  
  return { active: false, reason: 'No ?dev=1 in URL, no localStorage flag' };
}

/**
 * Get mapping object summary
 */
function getMappingSummary(state: MappingDiagnosticsState): {
  rawKeys: number;
  normalizedKeys: number;
  topLevelRawKeys: string[];
  topLevelNormalizedKeys: string[];
  rawJsonSize: number;
  normalizedJsonSize: number;
} {
  const rawKeys = state.rawInput ? Object.keys(state.rawInput).length : 0;
  const normalizedKeys = state.normalizedInput ? Object.keys(state.normalizedInput).length : 0;
  
  const topLevelRawKeys = state.rawInput ? Object.keys(state.rawInput).slice(0, 10) : [];
  const topLevelNormalizedKeys = state.normalizedInput ? Object.keys(state.normalizedInput).slice(0, 10) : [];
  
  const rawJson = JSON.stringify(state.rawInput || {});
  const normalizedJson = JSON.stringify(state.normalizedInput || {});
  
  return {
    rawKeys,
    normalizedKeys,
    topLevelRawKeys,
    topLevelNormalizedKeys,
    rawJsonSize: rawJson.length,
    normalizedJsonSize: normalizedJson.length,
  };
}

/**
 * Format timestamp to readable time
 */
function formatTime(ts: number): string {
  const date = new Date(ts);
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  return `${time}.${ms}`;
}

/**
 * Format timestamp as relative time
 */
function formatRelativeTime(ts: number): string {
  const now = Date.now();
  const diff = now - ts;
  
  if (diff < 1000) return 'just now';
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  return `${Math.floor(diff / 3600000)}h ago`;
}

/**
 * Compare two semver strings.
 * Returns -1 if a < b, 0 if equal, 1 if a > b.
 * Non-numeric or missing components are treated as 0.
 */
function compareSemver(a: string, b: string): -1 | 0 | 1 {
  const parse = (v: string): [number, number, number] => {
    const parts = v.split('.');
    return [
      parseInt(parts[0] ?? '0', 10) || 0,
      parseInt(parts[1] ?? '0', 10) || 0,
      parseInt(parts[2] ?? '0', 10) || 0,
    ];
  };
  const [aMaj, aMin, aPatch] = parse(a);
  const [bMaj, bMin, bPatch] = parse(b);
  if (aMaj !== bMaj) return aMaj > bMaj ? 1 : -1;
  if (aMin !== bMin) return aMin > bMin ? 1 : -1;
  if (aPatch !== bPatch) return aPatch > bPatch ? 1 : -1;
  return 0;
}

/**
 * Build a compact one-line contract version snapshot string for bug reports.
 */
function buildVersionSnapshotString(dataVersion: string, uiVersion: string, engineVersion: string, docVersion: string | null, migratedFromLegacy?: boolean): string {
  const docPart = docVersion
    ? migratedFromLegacy
      ? `Doc stamped: pre-contract → migrated to v${docVersion}`
      : `Doc stamped: v${docVersion}`
    : `Doc stamped: pre—Data Contract v${dataVersion} (migrated on load)`;
  return `Data Contract: v${dataVersion} | UI Mapping: v${uiVersion} | Engine Ruleset: v${engineVersion} | ${docPart}`;
}

/**
 * Copy text to clipboard
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      return success;
    } catch {
      return false;
    }
  }
}

// =============================================================================
// CONTRACT EXPLORER (light-theme version for white overlay)
// =============================================================================

const MAPPING_REGISTRY_PATH = 'apps/standalone-web/src/mapping/registry.ts';

function ContractExplorerLight() {
  const [query, setQuery] = useState('');
  const [coverageFilter, setCoverageFilter] = useState<'all' | 'wired' | 'ui-only' | 'engine-only' | 'mismatch'>('all');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<'coverage' | 'capabilities'>('coverage');
  const [copiedVersions, setCopiedVersions] = useState(false);
  const [copiedList, setCopiedList] = useState(false);
  const [copiedField, setCopiedField] = useState(false);
  const [copiedSource, setCopiedSource] = useState<string | null>(null);

  const DETAIL_SPLIT_MAX_HEIGHT = '45%';
  const dataContractMatch = ATLAS_DATA_CONTRACT_VERSION === ENGINE_RULESET_VERSION;

  const uiFields = questionMappings.map((m: QuestionMapping) => m.questionId);
  const engineFieldMap = new Map(engineCapabilities.supportedInputs.map((input) => [input.key, input]));

  const coverageRows = [
    ...questionMappings.map((mapping: QuestionMapping) => {
      const fieldDef = (contractFields as Record<string, import('../contract/atlasDataContract').FieldDef | undefined>)[mapping.questionId];
      const direct = engineFieldMap.get(mapping.questionId);
      const normalized = mapping.normalizedPaths.find((path) => engineFieldMap.has(path));
      const viaEngineKey = fieldDef?.engineKey ? engineFieldMap.get(fieldDef.engineKey) : undefined;
      const engine = direct ?? (normalized ? engineFieldMap.get(normalized) : undefined) ?? viaEngineKey;
      const mismatchReasons = computeMismatchReasons(mapping, engineCapabilities, fieldDef);
      // A field is ui-only when explicitly flagged in the FieldDef — not a mismatch.
      let status: 'wired' | 'ui-only' | 'mismatch' = engine ? 'wired' : 'ui-only';
      if (fieldDef?.uiOnly) status = 'ui-only';
      // Only flag as mismatch when the field is engine-bound but values disagree.
      if (!fieldDef?.uiOnly && mismatchReasons.length > 0) status = 'mismatch';
      return { key: mapping.questionId, label: mapping.label, section: mapping.section ?? 'unknown', status, notes: mapping.notes ?? fieldDef?.notes, mismatchReasons };
    }),
    ...engineCapabilities.supportedInputs
      .filter((input) => !uiFields.includes(input.key) && !questionMappings.some((m: QuestionMapping) => m.normalizedPaths.includes(input.key)))
      .map((input) => ({
        key: input.key, label: input.key, section: 'engine', status: 'engine-only' as const,
        notes: input.required ? 'Required by engine' : 'Optional engine capability', mismatchReasons: [] as string[],
      })),
  ];

  const counts = {
    wired: coverageRows.filter(r => r.status === 'wired').length,
    'ui-only': coverageRows.filter(r => r.status === 'ui-only').length,
    'engine-only': coverageRows.filter(r => r.status === 'engine-only').length,
    mismatch: coverageRows.filter(r => r.status === 'mismatch').length,
  };

  const filtered = coverageRows.filter((row) => {
    const q = query.trim().toLowerCase();
    const queryMatch = !q || row.key.toLowerCase().includes(q) || row.label.toLowerCase().includes(q) || row.section.toLowerCase().includes(q) || (row.notes ?? '').toLowerCase().includes(q);
    return queryMatch && (coverageFilter === 'all' || row.status === coverageFilter);
  });

  const selectedRow = selectedKey ? coverageRows.find(r => r.key === selectedKey) ?? null : null;
  const selectedMapping = selectedKey ? questionMappings.find((m: QuestionMapping) => m.questionId === selectedKey) ?? null : null;

  const badge = (status: string) => {
    if (status === 'wired') return { text: '✅ wired', bg: '#dcfce7', color: '#166534' };
    if (status === 'ui-only') return { text: '🟡 ui-only', bg: '#fef9c3', color: '#854d0e' };
    if (status === 'engine-only') return { text: '🟠 engine-only', bg: '#ffedd5', color: '#9a3412' };
    return { text: '🔴 mismatch', bg: '#fee2e2', color: '#991b1b' };
  };

  const handleCopyVersions = async () => {
    const ok = await copyToClipboard(formatVersionsSummary());
    if (ok) { setCopiedVersions(true); setTimeout(() => setCopiedVersions(false), 2000); }
  };
  const handleCopyList = async () => {
    const ok = await copyToClipboard(filtered.map(r => `${r.key} [${r.status}]`).join('\n'));
    if (ok) { setCopiedList(true); setTimeout(() => setCopiedList(false), 2000); }
  };
  const handleCopyFieldJson = async () => {
    if (!selectedMapping) return;
    const ok = await copyToClipboard(JSON.stringify(selectedMapping, null, 2));
    if (ok) { setCopiedField(true); setTimeout(() => setCopiedField(false), 2000); }
  };
  const handleCopySource = async (section: string) => {
    const ok = await copyToClipboard(SOURCE_FILE_PATHS[section] ?? MAPPING_REGISTRY_PATH);
    if (ok) { setCopiedSource(section); setTimeout(() => setCopiedSource(null), 2000); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '6px' }}>
          <span style={{ fontSize: '11px', color: '#6b7280' }}>Contract <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px', color: '#111827' }}>v{ATLAS_DATA_CONTRACT_VERSION}</code></span>
          <span style={{ fontSize: '11px', color: '#6b7280' }}>UI <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px', color: '#111827' }}>v{UI_MAPPING_VERSION}</code></span>
          <span style={{ fontSize: '11px', color: '#6b7280' }}>Engine <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px', color: '#111827' }}>v{ENGINE_RULESET_VERSION}</code></span>
          <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 700, background: dataContractMatch ? '#dcfce7' : '#fee2e2', color: dataContractMatch ? '#166534' : '#991b1b' }}>{dataContractMatch ? '✓ in sync' : '⚠ drift'}</span>
          <button onClick={handleCopyVersions} style={{ marginLeft: 'auto', padding: '2px 8px', border: '1px solid #d1d5db', background: copiedVersions ? '#10b981' : '#fff', color: copiedVersions ? '#fff' : '#374151', cursor: 'pointer', fontSize: '10px', borderRadius: '4px' }}>
            {copiedVersions ? 'Copied!' : 'Copy versions'}
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '4px', fontSize: '10px' }}>
          <div style={{ background: '#dcfce7', color: '#166534', padding: '3px 6px', borderRadius: '4px' }}>Wired: {counts.wired}</div>
          <div style={{ background: '#fef9c3', color: '#854d0e', padding: '3px 6px', borderRadius: '4px' }}>UI-only: {counts['ui-only']}</div>
          <div style={{ background: '#ffedd5', color: '#9a3412', padding: '3px 6px', borderRadius: '4px', cursor: 'pointer' }} onClick={() => { setCoverageFilter('engine-only'); setActiveSubTab('coverage'); }}>Engine-only: {counts['engine-only']}</div>
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '3px 6px', borderRadius: '4px' }}>Mismatch: {counts.mismatch}</div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
        {(['coverage', 'capabilities'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveSubTab(tab)} style={{ padding: '6px 10px', border: 'none', background: activeSubTab === tab ? '#fff' : 'transparent', color: activeSubTab === tab ? '#3b82f6' : '#6b7280', cursor: 'pointer', fontSize: '11px', fontWeight: activeSubTab === tab ? 600 : 400, borderBottom: activeSubTab === tab ? '2px solid #3b82f6' : 'none' }}>
            {tab === 'coverage' ? 'Coverage' : 'Capabilities'}
          </button>
        ))}
      </div>

      {activeSubTab === 'coverage' && (
        <>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb' }}>
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search coverage…" style={{ width: '100%', padding: '5px 8px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '4px', color: '#111827', fontSize: '11px', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: '4px', marginTop: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
              {['all', 'engine-only', 'ui-only', 'mismatch', 'wired'].map(f => (
                <button key={f} onClick={() => setCoverageFilter(f as typeof coverageFilter)} style={{ background: coverageFilter === f ? '#3b82f6' : '#f3f4f6', color: coverageFilter === f ? '#fff' : '#374151', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '10px', padding: '2px 6px', cursor: 'pointer' }}>{f}</button>
              ))}
              <button onClick={handleCopyList} style={{ marginLeft: 'auto', padding: '2px 8px', border: '1px solid #d1d5db', background: copiedList ? '#10b981' : '#fff', color: copiedList ? '#fff' : '#6b7280', cursor: 'pointer', fontSize: '10px', borderRadius: '4px' }}>{copiedList ? 'Copied!' : 'Copy list'}</button>
            </div>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ flex: selectedKey ? '0 0 auto' : 1, maxHeight: selectedKey ? DETAIL_SPLIT_MAX_HEIGHT : undefined, overflow: 'auto' }}>
              {filtered.map((row) => {
                const b = badge(row.status);
                const isSelected = row.key === selectedKey;
                return (
                  <div key={`${row.status}:${row.key}`} onClick={() => setSelectedKey(isSelected ? null : row.key)} style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', fontSize: '11px', cursor: 'pointer', background: isSelected ? '#eff6ff' : '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', gap: '8px' }}>
                      <code style={{ color: '#2563eb', fontWeight: 700, fontSize: '11px' }}>{row.key}</code>
                      <span style={{ background: b.bg, color: b.color, padding: '1px 5px', borderRadius: '4px', fontSize: '9px', fontWeight: 700 }}>{b.text}</span>
                    </div>
                    <div style={{ color: '#374151', fontSize: '11px', marginBottom: '2px' }}>{row.label}</div>
                    <div style={{ color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px' }}>
                      section: {row.section}
                      <button onClick={(e) => { e.stopPropagation(); handleCopySource(row.section); }} title={`Copy path: ${SOURCE_FILE_PATHS[row.section] ?? MAPPING_REGISTRY_PATH}`} style={{ padding: '1px 4px', border: '1px solid #d1d5db', background: copiedSource === row.section ? '#10b981' : '#fff', color: copiedSource === row.section ? '#fff' : '#9ca3af', cursor: 'pointer', fontSize: '9px', borderRadius: '3px' }}>{copiedSource === row.section ? '✓' : 'copy path'}</button>
                    </div>
                    {row.notes && <div style={{ marginTop: '2px', fontSize: '10px', color: '#9ca3af', fontStyle: 'italic' }}>{row.notes}</div>}
                    {row.status === 'mismatch' && row.mismatchReasons.length > 0 && <div style={{ marginTop: '2px', fontSize: '10px', color: '#dc2626' }}>⚠ {row.mismatchReasons[0]}</div>}
                  </div>
                );
              })}
            </div>

            {selectedRow && (
              <div style={{ flex: 1, overflow: 'auto', borderTop: '2px solid #e5e7eb', background: '#f9fafb', padding: '10px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <code style={{ color: '#2563eb', fontWeight: 700, fontSize: '12px' }}>{selectedRow.key}</code>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {selectedMapping && <button onClick={handleCopyFieldJson} style={{ padding: '2px 8px', border: '1px solid #d1d5db', background: copiedField ? '#10b981' : '#fff', color: copiedField ? '#fff' : '#6b7280', cursor: 'pointer', fontSize: '10px', borderRadius: '4px' }}>{copiedField ? 'Copied!' : 'Copy JSON'}</button>}
                    <button onClick={() => setSelectedKey(null)} style={{ padding: '2px 8px', border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', cursor: 'pointer', fontSize: '10px', borderRadius: '4px' }}>✕</button>
                  </div>
                </div>
                {selectedRow.mismatchReasons.length > 0 && (
                  <div style={{ marginBottom: '8px', padding: '8px', background: '#fee2e2', borderRadius: '4px', border: '1px solid #fca5a5' }}>
                    <div style={{ color: '#991b1b', fontWeight: 700, fontSize: '11px', marginBottom: '4px' }}>🔴 Mismatch details</div>
                    {selectedRow.mismatchReasons.map((reason, i) => <div key={i} style={{ color: '#dc2626', fontSize: '10px', marginBottom: '2px' }}>• {reason}</div>)}
                    <div style={{ marginTop: '6px', color: '#92400e', fontSize: '10px', fontWeight: 600 }}>Suggested fix:</div>
                    {selectedRow.mismatchReasons.map((reason, i) => {
                      let fix = '';
                      if (reason.includes('UI has enum values not in engine')) fix = 'Update engine enum or remove invalid UI values.';
                      else if (reason.includes('Engine has enum values not in UI')) fix = 'Add missing engine enum values to UI mapping.';
                      else if (reason.includes('Engine requires this field but UI marks it optional')) fix = 'Mark as required in UI mapping or make engine field optional.';
                      else fix = 'Review UI mapping and engine contract.';
                      return fix ? <div key={i} style={{ color: '#92400e', fontSize: '10px' }}>→ {fix}</div> : null;
                    })}
                  </div>
                )}
                {selectedMapping && (
                  <div style={{ fontSize: '10px', color: '#6b7280' }}>
                    {selectedMapping.engineImpact && selectedMapping.engineImpact.length > 0 && <div style={{ marginBottom: '4px' }}><strong style={{ color: '#374151' }}>Engine impact: </strong>{selectedMapping.engineImpact.join(', ')}</div>}
                    {selectedMapping.enums && <div style={{ marginBottom: '4px' }}><strong style={{ color: '#374151' }}>UI enums: </strong>{Object.entries(selectedMapping.enums).map(([k, vals]) => <span key={k}>{k}: [{vals.join(', ')}] </span>)}</div>}
                    <div style={{ marginBottom: '2px' }}><strong style={{ color: '#374151' }}>Required: </strong>{selectedMapping.required ? 'yes' : 'no'}{selectedMapping.uiOnly ? ' · UI-only' : ''}</div>
                    <div><strong style={{ color: '#374151' }}>Normalized paths: </strong>{selectedMapping.normalizedPaths.join(', ') || '(none)'}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {activeSubTab === 'capabilities' && (
        <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ color: '#111827', fontWeight: 700, fontSize: '12px', marginBottom: '8px' }}>Engine Supported Inputs</div>
            {engineCapabilities.supportedInputs.map(input => (
              <div key={input.key} style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', fontSize: '11px', display: 'flex', justifyContent: 'space-between' }}>
                <code style={{ color: '#2563eb' }}>{input.key}</code>
                <span style={{ color: input.required ? '#dc2626' : '#9ca3af', fontSize: '10px' }}>{input.required ? 'required' : 'optional'}</span>
              </div>
            ))}
          </div>
          <div>
            <div style={{ color: '#111827', fontWeight: 700, fontSize: '12px', marginBottom: '8px' }}>Engine Supported Enums</div>
            {Object.entries(engineCapabilities.supportedEnums).map(([enumKey, values]) => (
              <div key={enumKey} style={{ marginBottom: '10px' }}>
                <div style={{ color: '#d97706', fontWeight: 600, fontSize: '11px', marginBottom: '4px' }}>{enumKey}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {values.map(v => <span key={v} style={{ background: '#f3f4f6', color: '#374151', padding: '1px 6px', borderRadius: '3px', fontSize: '10px' }}>{v}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface EventItemProps {
  event: MappingLifecycleEvent;
}

function EventItem({ event }: EventItemProps) {
  const typeColors: Record<string, string> = {
    devMode_evaluated: '#3b82f6',
    doc_loaded: '#10b981',
    mapping_initialized: '#8b5cf6',
    mapping_updated: '#f59e0b',
    mapping_reset: '#ef4444',
    hydration_completed: '#06b6d4',
    store_created: '#6366f1',
    doc_save_requested: '#ec4899',
    doc_save_completed: '#14b8a6',
    api_fetch_started: '#f97316',
    api_fetch_completed: '#84cc16',
    section_saved: '#10b981',
  };
  
  const color = typeColors[event.type] || '#64748b';
  
  return (
    <div
      style={{
        padding: '8px 12px',
        borderBottom: '1px solid #e5e7eb',
        fontSize: '11px',
        background: '#fff',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span
          style={{
            color,
            fontWeight: 600,
            fontSize: '10px',
            textTransform: 'uppercase',
          }}
        >
          {event.type.replace(/_/g, ' ')}
        </span>
        <span style={{ color: '#9ca3af', fontSize: '10px' }}>
          {formatRelativeTime(event.ts)}
        </span>
      </div>
      
      <div style={{ color: '#374151', fontSize: '11px', marginBottom: '2px' }}>
        <strong>Source:</strong> {event.source}
      </div>
      
      {event.docId && (
        <div style={{ color: '#6b7280', fontSize: '10px' }}>
          <strong>DocID:</strong> {event.docId}
        </div>
      )}
      
      {event.section && (
        <div style={{ color: '#6b7280', fontSize: '10px' }}>
          <strong>Section:</strong> {event.section}
        </div>
      )}
      
      {event.keys !== undefined && (
        <div style={{ color: '#6b7280', fontSize: '10px' }}>
          <strong>Keys:</strong> {event.keys}
        </div>
      )}
      
      {event.fields && event.fields.length > 0 && (
        <div style={{ color: '#6b7280', fontSize: '10px' }}>
          <strong>Fields:</strong> {event.fields.length} updated
        </div>
      )}
      
      {event.note && (
        <div style={{ color: '#6b7280', fontSize: '10px', marginTop: '4px' }}>
          {event.note}
        </div>
      )}
      
      <div style={{ color: '#9ca3af', fontSize: '9px', marginTop: '2px' }}>
        {formatTime(event.ts)}
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function DevOverlay() {
  const [state, setState] = useState<MappingDiagnosticsState>(getDiagnosticsState);
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedVersions, setCopiedVersions] = useState(false);
  const [activeSection, setActiveSection] = useState<'overview' | 'events' | 'mapping' | 'contract'>('overview');
  
  // Subscribe to diagnostics state changes
  useEffect(() => {
    const unsubscribe = subscribe(setState);
    return unsubscribe;
  }, []);
  
  // Subscribe to overlay state changes (for auto-collapse on modal open)
  useEffect(() => {
    const unsubscribe = subscribeToOverlayState((shouldBeOpen) => {
      setIsOpen(shouldBeOpen);
    });
    return unsubscribe;
  }, []);
  
  // Notify devtools module when overlay state changes
  useEffect(() => {
    notifyOverlayStateChange(isOpen);
  }, [isOpen]);
  
  // Handle copy diagnostics
  const handleCopy = useCallback(async () => {
    const docId = getCurrentDocId();
    const devMode = getDevModeInfo();
    const summary = getMappingSummary(state);
    
    const diagnostics = {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      search: window.location.search,
      pathname: window.location.pathname,
      devMode: {
        active: devMode.active,
        reason: devMode.reason,
      },
      docId: docId || '<none>',
      mapping: {
        rawKeys: summary.rawKeys,
        normalizedKeys: summary.normalizedKeys,
        topLevelRawKeys: summary.topLevelRawKeys,
        topLevelNormalizedKeys: summary.topLevelNormalizedKeys,
        rawJsonSizeBytes: summary.rawJsonSize,
        normalizedJsonSizeBytes: summary.normalizedJsonSize,
      },
      events: state.lifecycleEvents.slice(-20).map(e => ({
        type: e.type,
        source: e.source,
        docId: e.docId,
        keys: e.keys,
        note: e.note,
        timestamp: new Date(e.ts).toISOString(),
        relativeTime: formatRelativeTime(e.ts),
      })),
      warnings: state.warnings.length,
      changes: state.lastChanges.length,
      rawInput: state.rawInput,
      normalizedInput: state.normalizedInput,
    };
    
    const success = await copyToClipboard(JSON.stringify(diagnostics, null, 2));
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [state]);
  
  // Don't render if not in dev mode
  const devMode = getDevModeInfo();
  if (!devMode.active) {
    return null;
  }
  
  const docId = getCurrentDocId();
  const summary = getMappingSummary(state);
  const recentEvents = state.lifecycleEvents.slice(-20).reverse();
  
  // Collapsed state - just show button
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        style={{
          position: 'fixed',
          top: '16px',
          right: '16px',
          background: '#3b82f6',
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          padding: '12px 16px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 700,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 10000,
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        🔧 DEV
      </button>
    );
  }
  
  // Expanded state - show overlay
  return (
    <div
      style={{
        position: 'fixed',
        top: '16px',
        right: '16px',
        width: 'min(450px, calc(100vw - 32px))',
        maxHeight: 'calc(100vh - 32px)',
        background: '#fff',
        border: '1px solid #d1d5db',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10000,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#f9fafb',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>🔧</span>
          <div>
            <div style={{ color: '#111827', fontWeight: 700, fontSize: '16px' }}>
              Dev Overlay
            </div>
            <div style={{ color: '#6b7280', fontSize: '11px' }}>
              Mobile-friendly diagnostics
            </div>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleCopy}
            style={{
              padding: '6px 12px',
              border: '1px solid #d1d5db',
              background: copied ? '#10b981' : '#fff',
              color: copied ? '#fff' : '#374151',
              cursor: 'pointer',
              fontSize: '11px',
              borderRadius: '6px',
              fontWeight: 500,
            }}
          >
            {copied ? '✓ Copied' : 'Copy JSON'}
          </button>
          
          <button
            onClick={() => setIsOpen(false)}
            style={{
              padding: '6px 12px',
              border: '1px solid #d1d5db',
              background: '#fff',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '11px',
              borderRadius: '6px',
            }}
          >
            Close
          </button>
        </div>
      </div>
      
      {/* Tab Navigation */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid #e5e7eb',
          background: '#f9fafb',
        }}
      >
        {(['overview', 'events', 'mapping', 'contract'] as const).map(section => (
          <button
            key={section}
            onClick={() => setActiveSection(section)}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              background: activeSection === section ? '#fff' : 'transparent',
              color: activeSection === section ? '#3b82f6' : '#6b7280',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: activeSection === section ? 600 : 400,
              borderBottom: activeSection === section ? '2px solid #3b82f6' : 'none',
            }}
          >
            {section.charAt(0).toUpperCase() + section.slice(1)}
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div style={{ flex: 1, overflow: activeSection === 'contract' ? 'hidden' : 'auto', display: 'flex', flexDirection: 'column', background: '#fff' }}>
        {activeSection === 'overview' && (
          <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ 
                fontSize: '13px', 
                fontWeight: 600, 
                color: '#111827',
                marginBottom: '8px',
              }}>
                📍 Location
              </h3>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                <strong>Search:</strong> {window.location.search || '<empty>'}
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                <strong>Path:</strong> {window.location.pathname}
              </div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ 
                fontSize: '13px', 
                fontWeight: 600, 
                color: '#111827',
                marginBottom: '8px',
              }}>
                🔧 Dev Mode
              </h3>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                <strong>Status:</strong>{' '}
                <span style={{ 
                  color: devMode.active ? '#10b981' : '#ef4444',
                  fontWeight: 600,
                }}>
                  {devMode.active ? 'ENABLED' : 'DISABLED'}
                </span>
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                <strong>Reason:</strong> {devMode.reason}
              </div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ 
                fontSize: '13px', 
                fontWeight: 600, 
                color: '#111827',
                marginBottom: '8px',
              }}>
                📄 Document
              </h3>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                <strong>DocID:</strong> {docId || '<none>'}
              </div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ 
                fontSize: '13px', 
                fontWeight: 600, 
                color: '#111827',
                marginBottom: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span>📋 Contract Versions</span>
                <button
                  onClick={async () => {
                    const versionString = buildVersionSnapshotString(
                      ATLAS_DATA_CONTRACT_VERSION,
                      UI_MAPPING_VERSION,
                      ENGINE_RULESET_VERSION,
                      state.docContractVersion,
                      state.migratedFromLegacy,
                    );
                    const success = await copyToClipboard(versionString);
                    if (success) {
                      setCopiedVersions(true);
                      setTimeout(() => setCopiedVersions(false), 2000);
                    }
                  }}
                  style={{
                    padding: '3px 8px',
                    border: '1px solid #d1d5db',
                    background: copiedVersions ? '#10b981' : '#fff',
                    color: copiedVersions ? '#fff' : '#374151',
                    cursor: 'pointer',
                    fontSize: '10px',
                    borderRadius: '4px',
                    fontWeight: 500,
                  }}
                >
                  {copiedVersions ? '✓ Copied' : 'Copy versions'}
                </button>
              </h3>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                <strong>Data Contract (shared truth):</strong>{' '}
                <code style={{ background: '#f3f4f6', padding: '1px 4px', borderRadius: '3px' }}>
                  v{ATLAS_DATA_CONTRACT_VERSION}
                </code>
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                <strong>UI Mapping (implementation detail):</strong>{' '}
                <code style={{ background: '#f3f4f6', padding: '1px 4px', borderRadius: '3px' }}>
                  v{UI_MAPPING_VERSION}
                </code>
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                <strong>Engine Ruleset (implementation detail):</strong>{' '}
                <code style={{ background: '#f3f4f6', padding: '1px 4px', borderRadius: '3px' }}>
                  v{ENGINE_RULESET_VERSION}
                </code>
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                <strong>Doc contract version:</strong>{' '}
                {state.docContractVersion ? (
                  <>
                    <code style={{ background: '#f3f4f6', padding: '1px 4px', borderRadius: '3px' }}>
                      v{state.docContractVersion}
                    </code>
                    {state.migratedFromLegacy && (
                      <span style={{
                        marginLeft: '6px',
                        padding: '1px 5px',
                        background: '#f0fdf4',
                        border: '1px solid #22c55e',
                        borderRadius: '3px',
                        fontSize: '9px',
                        color: '#15803d',
                        fontWeight: 600,
                      }}>
                        pre-contract → migrated to v{state.docContractVersion}
                      </span>
                    )}
                    {!state.migratedFromLegacy && state.docContractVersion !== ATLAS_DATA_CONTRACT_VERSION && (
                      <span style={{
                        marginLeft: '6px',
                        padding: '1px 5px',
                        background: '#fffbeb',
                        border: '1px solid #f59e0b',
                        borderRadius: '3px',
                        fontSize: '9px',
                        color: '#92400e',
                        fontWeight: 600,
                      }}>
                        ⚠️ Doc v{state.docContractVersion} → Data Contract v{ATLAS_DATA_CONTRACT_VERSION}
                      </span>
                    )}
                  </>
                ) : (
                  <span style={{
                    padding: '1px 5px',
                    background: '#fffbeb',
                    border: '1px solid #f59e0b',
                    borderRadius: '3px',
                    fontSize: '9px',
                    color: '#92400e',
                    fontWeight: 600,
                  }}>
                    ⚠️ pre—Data Contract v{ATLAS_DATA_CONTRACT_VERSION}; migrated on load
                  </span>
                )}
              </div>
              {ATLAS_DATA_CONTRACT_VERSION !== ENGINE_RULESET_VERSION && (() => {
                const cmp = compareSemver(ATLAS_DATA_CONTRACT_VERSION, ENGINE_RULESET_VERSION);
                const msg = cmp > 0
                  ? `Data Contract v${ATLAS_DATA_CONTRACT_VERSION} is ahead of Engine v${ENGINE_RULESET_VERSION} — Engine may ignore new fields (risk: missing features)`
                  : `Engine v${ENGINE_RULESET_VERSION} is ahead of Data Contract v${ATLAS_DATA_CONTRACT_VERSION} — Engine may assume fields UI can't populate (risk: wrong recommendations)`;
                return (
                  <div style={{
                    marginTop: '6px',
                    padding: '6px 10px',
                    background: '#fef2f2',
                    border: '1px solid #ef4444',
                    borderRadius: '6px',
                    fontSize: '10px',
                    color: '#991b1b',
                    fontWeight: 600,
                  }}>
                    ⚠️ {msg}
                  </div>
                );
              })()}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ 
                fontSize: '13px', 
                fontWeight: 600, 
                color: '#111827',
                marginBottom: '8px',
              }}>
                🔄 Normalization
              </h3>
              {summary.normalizedKeys === 0 ? (
                <div style={{
                  padding: '8px 10px',
                  background: '#fffbeb',
                  border: '1px solid #f59e0b',
                  borderRadius: '6px',
                  marginBottom: '8px',
                }}>
                  <div style={{ fontSize: '11px', color: '#92400e', fontWeight: 600 }}>
                    ⚠️ Raw/legacy mode — UI is sending un-normalized data
                  </div>
                  <div style={{ fontSize: '10px', color: '#78350f', marginTop: '4px' }}>
                    The engine expects normalized keys (e.g. <code>fabric_era: "1950s"</code>).
                    If the UI sends raw values (e.g. <code>Fabric era: "Post-war"</code>), the
                    engine may produce "Unknown" outputs in Heat Demand and other sections.
                  </div>
                </div>
              ) : (
                <div style={{
                  padding: '8px 10px',
                  background: '#f0fdf4',
                  border: '1px solid #10b981',
                  borderRadius: '6px',
                  marginBottom: '8px',
                }}>
                  <div style={{ fontSize: '11px', color: '#065f46', fontWeight: 600 }}>
                    ✅ Normalized — engine-ready data present
                  </div>
                </div>
              )}
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                <strong>Raw Input (CollectedData):</strong> exactly what the user typed or selected in the PWA — may include values like "ASHP", "ashp", or a blank space.
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                <strong>Normalized Input (SystemRecInput):</strong> the engine-ready version — filtered and formatted so the logic doesn't have to guess (e.g. <code>air_source_heat_pump</code>).
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                <strong>Contract:</strong> the engine expects a specific key (e.g. <code>fabric_era</code>) with a specific value (e.g. <code>1950s</code>). A mismatch leads to "Unknown" outputs. Use the Mapping tab to inspect both sides.
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ 
                fontSize: '13px', 
                fontWeight: 600, 
                color: '#111827',
                marginBottom: '8px',
              }}>
                🗺️ Mapping Summary
              </h3>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                <strong>Raw keys:</strong> {summary.rawKeys}
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                <strong>Normalized keys:</strong> {summary.normalizedKeys}
              </div>
              <div style={{ fontSize: '11px', marginBottom: '4px' }}>
                <strong>UI input source:</strong>{' '}
                <span style={{ 
                  color: summary.normalizedKeys > 0 ? '#10b981' : '#f59e0b',
                  fontWeight: 600,
                }}>
                  {summary.normalizedKeys > 0 ? 'mappingStore normalized' : 'raw/legacy'}
                </span>
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                <strong>Raw JSON size:</strong> {(summary.rawJsonSize / 1024).toFixed(1)} KB
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                <strong>Normalized JSON size:</strong> {(summary.normalizedJsonSize / 1024).toFixed(1)} KB
              </div>
              {summary.topLevelRawKeys.length > 0 && (
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '8px' }}>
                  <strong>Top raw keys:</strong>
                  <div style={{ 
                    marginTop: '4px',
                    padding: '6px',
                    background: '#f3f4f6',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                  }}>
                    {summary.topLevelRawKeys.join(', ')}
                    {summary.rawKeys > 10 && ` (+${summary.rawKeys - 10} more)`}
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <h3 style={{ 
                fontSize: '13px', 
                fontWeight: 600, 
                color: '#111827',
                marginBottom: '8px',
              }}>
                📊 Activity
              </h3>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                <strong>Events:</strong> {state.lifecycleEvents.length} total, {recentEvents.length} recent
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                <strong>Warnings:</strong> {state.warnings.length}
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                <strong>Changes:</strong> {state.lastChanges.length}
              </div>
            </div>
          </div>
        )}
        
        {activeSection === 'events' && (
          <div>
            {recentEvents.length === 0 ? (
              <div style={{ 
                padding: '32px', 
                textAlign: 'center', 
                color: '#9ca3af',
                fontSize: '12px',
              }}>
                No events recorded yet.
                <br />
                Events appear as you interact with the app.
              </div>
            ) : (
              recentEvents.map((event, i) => (
                <EventItem key={`${event.ts}-${i}`} event={event} />
              ))
            )}
          </div>
        )}
        
        {activeSection === 'mapping' && (
          <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ 
                fontSize: '13px', 
                fontWeight: 600, 
                color: '#111827',
                marginBottom: '8px',
              }}>
                Raw Input (CollectedData)
              </h3>
              {state.rawInput ? (
                <pre
                  style={{
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    background: '#f3f4f6',
                    padding: '12px',
                    borderRadius: '6px',
                    overflow: 'auto',
                    maxHeight: '200px',
                    color: '#374151',
                    margin: 0,
                  }}
                >
                  {JSON.stringify(state.rawInput, null, 2)}
                </pre>
              ) : (
                <div style={{ 
                  fontSize: '11px', 
                  color: '#9ca3af',
                  fontStyle: 'italic',
                }}>
                  No raw input data yet
                </div>
              )}
            </div>
            
            <div>
              <h3 style={{ 
                fontSize: '13px', 
                fontWeight: 600, 
                color: '#111827',
                marginBottom: '8px',
              }}>
                Normalized Input (SystemRecInput)
              </h3>
              {state.normalizedInput ? (
                <pre
                  style={{
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    background: '#f3f4f6',
                    padding: '12px',
                    borderRadius: '6px',
                    overflow: 'auto',
                    maxHeight: '200px',
                    color: '#374151',
                    margin: 0,
                  }}
                >
                  {JSON.stringify(state.normalizedInput, null, 2)}
                </pre>
              ) : (
                <div style={{ 
                  fontSize: '11px', 
                  color: '#9ca3af',
                  fontStyle: 'italic',
                }}>
                  No normalized input data yet
                </div>
              )}
            </div>
          </div>
        )}

        {activeSection === 'contract' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <ContractExplorerLight />
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '12px 16px',
          borderTop: '1px solid #e5e7eb',
          fontSize: '10px',
          color: '#9ca3af',
          background: '#f9fafb',
        }}
      >
        Safe for production • Read-only • No PII • <a href={buildDevUrl()} style={{ color: '#9ca3af', textDecoration: 'underline' }}>?dev=1 to enable</a>
      </div>
    </div>
  );
}

export default DevOverlay;
